import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { TrafficLight, getOilStatus } from "@/components/TrafficLight";
import { ArrowLeftIcon, PlusIcon, WrenchIcon, PrinterIcon, MapPinIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { TripFormDialog } from "@/components/TripFormDialog";
import { OilChangeDialog } from "@/components/OilChangeDialog";
import { printTripReceipt } from "@/lib/printUtils";
import { toast } from "sonner";

export const CarDetail = () => {
  const { carId } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [car, setCar] = useState(null);
  const [trips, setTrips] = useState([]);
  const [oils, setOils] = useState([]);
  const [openTrip, setOpenTrip] = useState(false);
  const [openOil, setOpenOil] = useState(false);
  const [gpsSyncing, setGpsSyncing] = useState(false);

  const load = async () => {
    const [c, tr, ol] = await Promise.all([
      api.get(`/cars/${carId}`),
      api.get(`/trips?car_id=${carId}`),
      api.get(`/oil-changes?car_id=${carId}`),
    ]);
    setCar(c.data);
    setTrips(tr.data);
    setOils(ol.data);
  };

  useEffect(() => { load(); }, [carId]);

  const syncGps = async () => {
    setGpsSyncing(true);
    try {
      const { data } = await api.post(`/cars/${carId}/gps/sync`);
      if (data.ok) {
        toast.success(t("gpsSynced"));
        await load();
      } else {
        const map = {
          no_device: t("gpsNoDevice"),
          gps_error: data.error || t("gpsSyncFailed"),
        };
        toast.error(map[data.error_code] || data.error || t("gpsSyncFailed"), { duration: 7000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("gpsSyncFailed"));
    } finally {
      setGpsSyncing(false);
    }
  };

  if (!car) return <div className="text-zinc-500 text-sm uppercase tracking-[0.3em]">Loading…</div>;

  const st = getOilStatus(car);
  const nf = (n) => (n ?? 0).toLocaleString(lang === "ar" ? "ar-EG" : "en-US");

  return (
    <div className="space-y-8" data-testid="car-detail-root">
      <Link to="/cars" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white">
        <ArrowLeftIcon size={14} className="rtl-flip" /> {t("cars")}
      </Link>

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">{car.make} {car.model} · {car.year || "—"}</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">{car.plate_number}</h1>
        </div>
        <div className="flex gap-2">
          {car.gps_device_id && (
            <button
              onClick={syncGps}
              disabled={gpsSyncing}
              data-testid="gps-sync-btn"
              className="inline-flex items-center gap-2 border border-white/10 bg-[#121212] hover:bg-[#1C1C1C] px-4 py-3 text-sm disabled:opacity-60"
            >
              <ArrowsClockwiseIcon size={16} weight="bold" className={gpsSyncing ? "animate-spin" : ""} />
              {gpsSyncing ? t("gpsSyncing") : t("gpsSync")}
            </button>
          )}
          <button
            data-testid="record-oil-btn"
            onClick={() => setOpenOil(true)}
            className="inline-flex items-center gap-2 border border-white/10 bg-[#121212] hover:bg-[#1C1C1C] px-4 py-3 text-sm"
          >
            <WrenchIcon size={16} weight="bold" /> {t("recordOilChange")}
          </button>
          <button
            data-testid="log-return-btn"
            onClick={() => setOpenTrip(true)}
            className="inline-flex items-center gap-2 bg-white text-black px-4 py-3 font-semibold hover:bg-zinc-100 transition-colors"
          >
            <PlusIcon size={16} weight="bold" /> {t("logReturn")}
          </button>
        </div>
      </div>

      {/* Status Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 border border-white/10 bg-[#121212]">
        <Cell label={t("currentKm")} value={`${nf(car.current_km)} ${t("km")}`} />
        <Cell label={t("lastOilKm")} value={`${nf(car.last_oil_change_km)} ${t("km")}`} />
        <Cell
          label={t("lastOilDate")}
          value={
            car.last_oil_change_date
              ? new Date(car.last_oil_change_date).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US")
              : "—"
          }
        />
        <Cell label={t("intervalKm")} value={`${nf(car.oil_change_interval_km)} ${t("km")}`} />
        <Cell
          label={t("remainingKm")}
          value={
            <div className="flex items-center gap-3">
              <span className={st.level === "red" ? "text-[#FF3B30]" : st.level === "yellow" ? "text-[#FFEA00]" : "text-[#39FF14]"}>
                {st.remaining <= 0 ? `-${nf(Math.abs(st.remaining))}` : nf(st.remaining.toFixed(0))} {t("km")}
              </span>
              <TrafficLight status={st.level} size="sm" />
            </div>
          }
          last
        />
      </div>

      {/* Trip History */}
      <section className="border border-white/10 bg-[#121212]">
        <div className="px-6 py-4 border-b border-white/10">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/history</div>
          <h2 className="text-xl font-semibold">{t("tripHistory")}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full data-table text-sm">
            <thead>
              <tr className="text-zinc-500 text-xs uppercase tracking-[0.2em]">
                <th className="text-start px-4 py-3">{t("date")}</th>
                <th className="text-start px-4 py-3">{t("renterName")}</th>
                <th className="text-start px-4 py-3">{t("returnKm")}</th>
                <th className="text-start px-4 py-3">{t("kmDriven")}</th>
                <th className="text-start px-4 py-3">{t("notes")}</th>
                <th className="text-end px-4 py-3">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {trips.map((t2) => (
                <tr key={t2.trip_id} className="hover:bg-[#1C1C1C]" data-testid={`trip-row-${t2.trip_id}`}>
                  <td className="px-4 py-3 text-zinc-300">{new Date(t2.return_date).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}</td>
                  <td className="px-4 py-3 text-zinc-300">{t2.renter_name || "—"}</td>
                  <td className="px-4 py-3 font-semibold">{nf(t2.return_km)}</td>
                  <td className="px-4 py-3 text-[#39FF14]">+{nf(t2.km_driven)}</td>
                  <td className="px-4 py-3 text-zinc-500 max-w-xs truncate">{t2.notes || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button
                        onClick={() => printTripReceipt({ trip: t2, car, user, lang })}
                        data-testid={`trip-print-${t2.trip_id}`}
                        title={t("printReceipt")}
                        className="p-2 border border-white/10 hover:bg-[#1C1C1C]"
                      >
                        <PrinterIcon size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {trips.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500">{t("noTrips")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Oil change history */}
      {oils.length > 0 && (
        <section className="border border-white/10 bg-[#121212]">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-xl font-semibold">{t("maintenance")}</h2>
          </div>
          <table className="w-full data-table text-sm">
            <thead>
              <tr className="text-zinc-500 text-xs uppercase tracking-[0.2em]">
                <th className="text-start px-4 py-3">{t("date")}</th>
                <th className="text-start px-4 py-3">{t("oilChangeAtKm")}</th>
                <th className="text-start px-4 py-3">{t("notes")}</th>
              </tr>
            </thead>
            <tbody>
              {oils.map((o) => (
                <tr key={o.record_id} className="hover:bg-[#1C1C1C]">
                  <td className="px-4 py-3 text-zinc-300">{new Date(o.changed_date).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US")}</td>
                  <td className="px-4 py-3 font-semibold">{nf(o.changed_at_km)}</td>
                  <td className="px-4 py-3 text-zinc-500">{o.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <TripFormDialog open={openTrip} onOpenChange={setOpenTrip} car={car} onSaved={() => { setOpenTrip(false); load(); }} />
      <OilChangeDialog open={openOil} onOpenChange={setOpenOil} car={car} onSaved={() => { setOpenOil(false); load(); }} />

      {car.gps_device_id && (
        <section className="border border-white/10 bg-[#121212]" data-testid="gps-panel">
          <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
            <div className="p-2 border border-white/10 bg-[#0A0A0A]">
              <MapPinIcon size={20} weight="bold" style={{ color: "var(--primary-action, #007AFF)" }} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">MadarTech IVMS</div>
              <h2 className="text-lg font-semibold">{t("gpsSection")}</h2>
            </div>
          </div>
          <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">{t("gpsDeviceId")}</div>
              <div className="font-mono text-base" dir="ltr">{car.gps_device_id}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">{t("gpsOdometer")}</div>
              <div className="font-semibold text-base">{car.gps_last_km ? `${nf(car.gps_last_km)} ${t("km")}` : "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">{t("gpsLastSync")}</div>
              <div className="text-zinc-300 text-xs" dir="ltr">
                {car.gps_last_synced_at ? new Date(car.gps_last_synced_at).toLocaleString(lang === "ar" ? "ar-EG" : "en-US") : "—"}
              </div>
            </div>
            {car.gps_last_lat != null && car.gps_last_lon != null && (
              <div className="md:col-span-3">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">{t("gpsPosition")}</div>
                <a
                  href={`https://www.google.com/maps?q=${car.gps_last_lat},${car.gps_last_lon}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="gps-map-link"
                  dir="ltr"
                  className="inline-flex items-center gap-2 border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] px-3 py-2 text-xs font-mono transition-colors"
                >
                  <MapPinIcon size={12} weight="bold" />
                  {Number(car.gps_last_lat).toFixed(5)}, {Number(car.gps_last_lon).toFixed(5)} · {t("viewOnMap")} →
                </a>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

const Cell = ({ label, value, last }) => (
  <div className={`p-6 ${last ? "" : "border-e border-white/10"} border-b md:border-b-0 border-white/10`}>
    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</div>
    <div className="text-2xl font-bold">{value}</div>
  </div>
);
